/**
 * The dock's send path: the re-entrancy guard, the display/payload split, the
 * context envelope, and the queued handoff from another surface.
 *
 * Extracted from `Dock.tsx` by TASK-794. It is grouped as one unit because every
 * part of it exists to get ONE turn onto the wire correctly, and because three
 * of this epic's defects lived in the seams between those parts:
 *
 * - the guard (`isStreaming`) was dead for the life of the dock (TASK-765),
 * - the bubble and the wire payload were the same string (TASK-767),
 * - the page override could displace the authoritative `task_id` (TASK-788).
 */

import { useEffect } from 'react';
import {
  generateChatMessageId,
  resolvePrompt,
  promptMessageContent,
  type RequestedPrompt,
} from '../../stores/createChatStore';
import { useDockStore } from '../../stores/dockStore';
import type { ChatStoreState } from '../../stores/createChatStore';
import type { DockSideChannels } from '../../stores/dockChatStores';
import type { DockScope } from './dockScope';

type DockStoreHandle = { getState: () => ChatStoreState<DockSideChannels> };

export interface UseDockSendArgs {
  store: DockStoreHandle;
  /** The route's scope — what the advisor is looking AT. */
  placement: DockScope;
  /** The resolved thread scope — which conversation is in the surface. */
  scope: DockScope;
  /** Enriched client name for the entity envelope, when known. */
  clientName?: string;
  routeWithQuery: string;
  language: string;
  isStreaming: boolean;
  wsSendMessage: (
    message: string,
    conversationId?: string | null,
    context?: Record<string, unknown>
  ) => boolean;
  connect: () => void;
  onSend: () => void;
}

export function useDockSend({
  store,
  placement,
  scope,
  clientName,
  routeWithQuery,
  language,
  isStreaming,
  wsSendMessage,
  connect,
  onSend,
}: UseDockSendArgs): (prompt: RequestedPrompt) => void {
  const send = (prompt: RequestedPrompt) => {
    const state = store.getState();
    if (state.isStreaming) return;
    // TASK-767: `payload` goes on the wire (routing depends on its exact shape);
    // `display` is what the advisor sees. Identical for anything they typed.
    const { display, payload } = resolvePrompt(prompt);
    onSend();
    state.addMessage({
      id: generateChatMessageId('dock'),
      role: 'user',
      ...promptMessageContent({ display, payload }),
      timestamp: new Date().toISOString(),
    });
    // TASK-792: a turn begins in `routing`. This is the window TASK-765 exposed
    // — between send and the first token, spanning classification and every tool
    // call — which a boolean could not name.
    state.setTurn('routing');
    state.setError(null);

    // TASK-715: a live provider (page/popup with send-time state — brief snapshot,
    // portfolio, proposals) wins over the static override.
    const dockState = useDockStore.getState();
    const override = dockState.contextProvider?.() ?? dockState.contextOverride;
    // Placement first: the route's entity is what the advisor is looking AT.
    // The thread's entity (a task's client) fills in only where the route names
    // none — which the precedence rule guarantees is the same client or no
    // client at all.
    const entity = override?.entity ?? placement.entity ?? scope.entity;
    const context = {
      // Query string included so tab-scoped surfaces route deterministically (TASK-579).
      current_route: override?.current_route ?? routeWithQuery,
      current_entity: entity
        ? {
            ...entity,
            name: entity.type === 'client' ? (entity.name ?? clientName) : entity.name,
          }
        : undefined,
      page_data: {
        ...(placement.portfolioId ? { portfolio_id: placement.portfolioId } : {}),
        ...(override?.page_data ?? {}),
        // TASK-769: `page_data.task_id` is AUTHORITATIVE for work_task binding —
        // the resumed thread is context, never the claim.
        // TASK-788: it spreads AFTER the page override, so that authority is
        // enforced rather than asserted. Before this the override came last, and
        // any contextProvider returning a `task_id` would have silently rebound
        // the claim to a different task — no provider does today, which is
        // exactly why it would have been relied on.
        ...(scope.scopeType === 'task' && scope.entityId ? { task_id: scope.entityId } : {}),
        language,
      },
    };

    const sent = wsSendMessage(payload, state.conversationId, context);
    if (!sent) {
      state.setError('Not connected — trying to reconnect…');
      state.setTurn('error');
      connect();
    }
  };

  // A prompt handed over by another surface (TaskCard's "Work on this with AI").
  // Cleared BEFORE sending so a re-render can never replay it; a prompt arriving
  // mid-turn waits for the current one to finish rather than being dropped.
  const pendingPrompt = useDockStore((s) => s.pendingPrompt);
  const clearPendingPrompt = useDockStore((s) => s.clearPendingPrompt);
  useEffect(() => {
    if (!pendingPrompt || isStreaming) return;
    clearPendingPrompt();
    send(pendingPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt, isStreaming]);

  return send;
}
